import { defaultFilters } from './filters.model';
import type { FiltersForm, StoredFilters } from './types';
import { validationSchema } from './validation';

type BuildingOption = FiltersForm['houses'][number];
type StringOption = { id: string; label: string };
type NumberOption = { id: number; label: string };

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toArray = (value: unknown) => (Array.isArray(value) ? value : []);

const getOptionId = (option: unknown) => (isObject(option) ? option.id : option);

const getOptionLabel = (option: unknown) => {
  if (!isObject(option)) {
    return '';
  }

  const label = option.label ?? option.title ?? option.fullname;

  return label === null || label === undefined ? '' : String(label);
};

const toStringId = (value: unknown) =>
  value === null || value === undefined || value === '' ? null : String(value);

const toNumberId = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const id = Number(value);

  return Number.isFinite(id) ? id : null;
};

const normalizeStringIds = (value: unknown) =>
  toArray(value)
    .map((item) => toStringId(getOptionId(item)))
    .filter((id): id is string => id !== null);

const normalizeNumberIds = (value: unknown) =>
  toArray(value)
    .map((item) => toNumberId(getOptionId(item)))
    .filter((id): id is number => id !== null);

const normalizeStringOptions = (value: unknown) =>
  toArray(value)
    .map((option) => ({
      id: toStringId(getOptionId(option)),
      label: getOptionLabel(option),
    }))
    .filter((option): option is StringOption => option.id !== null);

const normalizeNumberOptions = (value: unknown) =>
  toArray(value)
    .map((option) => ({
      id: toNumberId(getOptionId(option)),
      label: getOptionLabel(option),
    }))
    .filter((option): option is NumberOption => option.id !== null);

const normalizeSelectValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }

  if (isObject(value)) {
    return normalizeSelectValue(value.id ?? value.value);
  }

  return String(value);
};

const normalizeStringOption = (value: unknown) => {
  const id = isObject(value) ? normalizeSelectValue(getOptionId(value)) : null;

  return id === null ? null : { id, label: getOptionLabel(value) };
};

const normalizeBuildings = (value: unknown) =>
  toArray(value)
    .map((option) => {
      if (!isObject(option)) {
        return null;
      }

      const id = toNumberId(option.id);
      const complexId = toNumberId(
        option.complexId ?? (isObject(option.complex) ? option.complex.id : null)
      );

      return id === null || complexId === null
        ? null
        : { id, title: getOptionLabel(option), complexId };
    })
    .filter((option): option is BuildingOption => option !== null);

const normalizeDate = (value: unknown) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(String(value));

  return Number.isNaN(date.getTime()) ? null : date;
};

const migrateStoredFilters = (savedFilters: StoredFilters): FiltersForm => {
  const savedPeriod = Array.isArray(savedFilters.periodOfCreation)
    ? savedFilters.periodOfCreation
    : [];

  const [createdAtFrom, createdAtTo] = savedPeriod.some(Boolean)
    ? savedPeriod
    : [savedFilters.created_at_from, savedFilters.created_at_to];

  return {
    periodOfCreation: [normalizeDate(createdAtFrom), normalizeDate(createdAtTo)],
    statuses: normalizeStringOptions(savedFilters.statuses),
    isTehzor: Boolean(savedFilters.isTehzor),
    isSupportActive: Boolean(savedFilters.isSupportActive),
    priorities: normalizeStringOptions(savedFilters.priorities),
    classes: normalizeStringOptions(savedFilters.classes),
    types: normalizeStringIds(savedFilters.types),
    payment: normalizeSelectValue(savedFilters.payment),
    invoiceStatus: normalizeStringOption(savedFilters.invoiceStatus),
    objects: normalizeNumberIds(savedFilters.objects),
    houses: normalizeBuildings(savedFilters.houses),
    apartments: normalizeNumberOptions(savedFilters.apartments),
    property: normalizeNumberIds(savedFilters.property),
    initiators: normalizeNumberOptions(savedFilters.initiators),
    stuffWriteOffNeeded: normalizeSelectValue(
      savedFilters.stuffWriteOffNeeded ?? savedFilters.stuff_write_off_needed
    ),
    expired: normalizeSelectValue(savedFilters.expired),
    assignees: normalizeNumberOptions(savedFilters.assignees),
    rates: normalizeStringOptions(savedFilters.rates),
    isUnreadMessage: Boolean(savedFilters.isUnreadMessage),
    hasRelated: Boolean(savedFilters.hasRelated ?? savedFilters.has_related),
  };
};

const resetInvalidValues = (filters: FiltersForm) => {
  const result = validationSchema.safeParse(filters);

  if (result.success) {
    return filters;
  }

  const normalizedFilters = { ...filters };

  result.error.issues.forEach(({ path }) => {
    const key = path[0] as keyof FiltersForm;

    if (key in defaultFilters) {
      Object.assign(normalizedFilters, { [key]: defaultFilters[key] });
    }
  });

  return normalizedFilters;
};

export const normalizeStoredFilters = (filters: StoredFilters) =>
  resetInvalidValues(migrateStoredFilters(filters));
